import React, { useState } from "react";
import {
  Calendar,
  Users,
  ArrowRight,
  Clock,
  User,
  Mail,
  Plane,
  Package,
  Car,
  Luggage,
} from "lucide-react";
import CityAutocomplete from "./CityAutocomplete";
import PhoneInput from "./PhoneInput";
import CustomDropdown from "./CustomDropdown";
import {
  getTodayDateString,
  getMinTimeForToday,
  isToday,
  isValidDateTime,
} from "../utils/dateTimeUtils";
import {
  CityResult,
  calculateDistance,
  estimateDuration,
} from "../utils/distanceCalculator";
import {
  VehicleData,
  PricingResult,
  calculateAllVehiclePrices,
  DEFAULT_VEHICLES,
  getPriceBreakdown,
} from "../utils/pricingCalculator";
import { useTranslation } from "react-i18next";

interface FormData {
  pickup: string;
  destination: string;
  flightNumber: string;
  date: string;
  time: string;
  passengers: string;
  luggage: string;
  firstName: string;
  lastName: string;
  contactEmail: string;
  contactPhone: string;
  selectedVehicleId: number | null;
}

interface CityCoords {
  pickup?: { lat: number; lon: number };
  destination?: { lat: number; lon: number };
}

interface BookingFormProps {
  onBookingComplete?: () => void;
}

const BookingForm: React.FC<BookingFormProps> = ({ onBookingComplete }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<FormData>({
    pickup: "",
    destination: "",
    flightNumber: "",
    date: "",
    time: "",
    passengers: "",
    luggage: "",
    firstName: "",
    lastName: "",
    contactEmail: "",
    contactPhone: "",
    selectedVehicleId: null,
  });

  // Dropdown options
  const passengerOptions = [
    { value: "1", label: t("1201", "1 passenger") },
    { value: "2", label: t("1202", "2 passengers") },
    { value: "3", label: t("1203", "3 passengers") },
    { value: "4", label: t("1204", "4 passengers") },
    { value: "5", label: t("1205", "5 passengers") },
    { value: "6", label: t("1206", "6 passengers") },
    { value: "7", label: t("1207", "7 passengers") },
    { value: "8", label: t("1208", "8 passengers") },
  ];

  const luggageOptions = [
    { value: "1", label: t("1209", "1 piece") },
    { value: "2", label: t("1210", "2 pieces") },
    { value: "3", label: t("1211", "3 pieces") },
    { value: "4", label: t("1212", "4 pieces") },
    { value: "5", label: t("1213", "5 pieces") },
    { value: "6", label: t("1214", "6 pieces") },
    { value: "7", label: t("1215", "7 pieces") },
    { value: "8", label: t("1216", "8 pieces") },
    { value: "9", label: t("1217", "9 pieces") },
    { value: "10", label: t("1218", "10 pieces") },
  ];

  const [cityCoords, setCityCoords] = useState<CityCoords>({});
  const [distanceInfo, setDistanceInfo] = useState<{
    distance: number;
    duration: string;
  } | null>(null);
  const [vehiclePricing, setVehiclePricing] = useState<PricingResult[]>([]);
  const [selectedVehicle, setSelectedVehicle] = useState<VehicleData | null>(
    null
  );
  const [step, setStep] = useState(1); // 1: Trip details, 2: Vehicle selection, 3: Contact info, 4: Confirmation
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [dateTimeError, setDateTimeError] = useState("");

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear datetime error when user changes date or time
    if (name === "date" || name === "time") {
      setDateTimeError("");
    }
  };

  const calculateRouteInfo = (
    pickup?: { lat: number; lon: number },
    destination?: { lat: number; lon: number }
  ) => {
    if (pickup && destination) {
      const { km } = calculateDistance(
        pickup.lat,
        pickup.lon,
        destination.lat,
        destination.lon
      );
      const duration = estimateDuration(km);
      setDistanceInfo({ distance: km, duration });

      // Calculate pricing for all vehicles
      const pricing = calculateAllVehiclePrices(
        DEFAULT_VEHICLES,
        pickup.lat,
        pickup.lon,
        destination.lat,
        destination.lon
      );
      setVehiclePricing(pricing);
    } else {
      setDistanceInfo(null);
      setVehiclePricing([]);
    }
  };

  const handleCitySelect = (
    field: "pickup" | "destination",
    value: string,
    cityData?: CityResult
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    if (cityData) {
      const newCoords = {
        lat: parseFloat(cityData.lat),
        lon: parseFloat(cityData.lon),
      };

      setCityCoords((prev) => {
        const updatedCoords = { ...prev, [field]: newCoords };
        // Calculate distance when both coordinates are available
        calculateRouteInfo(updatedCoords.pickup, updatedCoords.destination);
        return updatedCoords;
      });
    }
  };

  const validateDateTime = (): boolean => {
    if (!formData.date || !formData.time) {
      setDateTimeError(t("1219", "Please select both date and time"));
      return false;
    }

    if (!isValidDateTime(formData.date, formData.time)) {
      setDateTimeError(
        t("1220", "Please select a date and time at least 30 minutes from now")
      );
      return false;
    }

    setDateTimeError("");
    return true;
  };

  const handleNext = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateDateTime()) {
      setStep(2);
    }
  };

  const handleVehicleSelect = (vehicle: VehicleData) => {
    setSelectedVehicle(vehicle);
    setFormData((prev) => ({ ...prev, selectedVehicleId: vehicle.id }));
    setStep(3);
  };

  const handleConfirm = () => {
    setStep(4);
  };

  const handleEdit = () => {
    if (step === 4) setStep(3);
    else if (step === 3) setStep(2);
    else setStep(1);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      const response = await fetch("/api/booking", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          pickup: formData.pickup,
          destination: formData.destination,
          flightNumber: formData.flightNumber,
          date: formData.date,
          time: formData.time,
          passengers: formData.passengers,
          luggage: formData.luggage,
          contactName: `${formData.firstName} ${formData.lastName}`,
          contactEmail: formData.contactEmail,
          contactPhone: formData.contactPhone,
          distance: distanceInfo?.distance,
          duration: distanceInfo?.duration,
          selectedVehicle: selectedVehicle
            ? {
                id: selectedVehicle.id,
                name: selectedVehicle.name,
                type: selectedVehicle.type,
              }
            : null,
          pricing:
            selectedVehicle &&
            vehiclePricing.find((p) => p.vehicleId === selectedVehicle.id)
              ? getPriceBreakdown(
                  vehiclePricing.find(
                    (p) => p.vehicleId === selectedVehicle.id
                  )!
                )
              : null,
          coordinates: cityCoords,
          source: "taxischipholairport", // Identify this site
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to submit booking");
      }

      setIsSubmitting(false);
      setIsSuccess(true);

      // Call callback if provided (for modal)
      if (onBookingComplete) {
        setTimeout(() => {
          onBookingComplete();
          // Reset form state when closing modal
          setFormData({
            pickup: "",
            destination: "",
            flightNumber: "",
            date: "",
            time: "",
            passengers: "",
            luggage: "",
            firstName: "",
            lastName: "",
            contactEmail: "",
            contactPhone: "",
            selectedVehicleId: null,
          });
          setCityCoords({});
          setDistanceInfo(null);
          setVehiclePricing([]);
          setSelectedVehicle(null);
          setStep(1);
          setIsSuccess(false);
        }, 2000);
      } else {
        // Reset after showing success message (for standalone page)
        setTimeout(() => {
          setFormData({
            pickup: "",
            destination: "",
            flightNumber: "",
            date: "",
            time: "",
            passengers: "",
            luggage: "",
            firstName: "",
            lastName: "",
            contactEmail: "",
            contactPhone: "",
            selectedVehicleId: null,
          });
          setCityCoords({});
          setDistanceInfo(null);
          setVehiclePricing([]);
          setSelectedVehicle(null);
          setStep(1);
          setIsSuccess(false);
        }, 3000);
      }
    } catch (error) {
      setIsSubmitting(false);
      console.error("Booking submission error:", error);
      alert("Failed to submit booking. Please try again.");
    }
  };

  if (isSuccess) {
    return (
      <div className="text-center py-8">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
          <svg
            className="w-8 h-8 text-green-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M5 13l4 4L19 7"
            ></path>
          </svg>
        </div>
        <h2 className="text-xl font-bold text-white mb-2">
          {t("1291", "Booking Request Sent!")}
        </h2>
        <p className="text-gray-300 mb-4">
          {t(
            "1221",
            "Thank you for your booking. We'll contact you shortly to confirm your taxi reservation."
          )}
        </p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-xl font-bold text-white mb-2">
        {t("1222", "Book Your Taxi Ride")}
      </h2>
      <p className="text-gray-300 mb-6">
        {t("1223", "Get a reliable taxi service to and from Schiphol Airport")}
      </p>

      {/* Progress Indicator */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span
            className={`text-sm font-medium ${
              step >= 1 ? "text-(--primary)" : "text-gray-400"
            }`}
          >
            {t("1224", "Trip Details")}
          </span>
          <span
            className={`text-sm font-medium ${
              step >= 2 ? "text-(--primary)" : "text-gray-400"
            }`}
          >
            {t("1225", "Vehicle & Pricing")}
          </span>
          <span
            className={`text-sm font-medium ${
              step >= 3 ? "text-(--primary)" : "text-gray-400"
            }`}
          >
            {t("1226", "Contact Info")}
          </span>
          <span
            className={`text-sm font-medium ${
              step >= 4 ? "text-(--primary)" : "text-gray-400"
            }`}
          >
            {t("1227", "Confirmation")}
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-(--primary) h-2 rounded-full transition-all duration-300"
            style={{ width: `${((step - 1) / 3) * 100}%` }}
          ></div>
        </div>
      </div>

      {step === 1 && (
        <form onSubmit={handleNext}>
          <div className="mb-4">
            <CityAutocomplete
              value={formData.pickup}
              onChange={(value, cityData) =>
                handleCitySelect("pickup", value, cityData)
              }
              placeholder={t("1228", "Schiphol Airport, Amsterdam, etc.")}
              label={t("1229", "Pickup Location")}
              required
            />
          </div>

          <div className="mb-4">
            <CityAutocomplete
              value={formData.destination}
              onChange={(value, cityData) =>
                handleCitySelect("destination", value, cityData)
              }
              placeholder={t("1230", "Utrecht, Rotterdam, etc.")}
              label={t("1231", "Destination")}
              required
            />
          </div>

          {/* Distance and Duration Info */}
          {distanceInfo && (
            <div className="mb-4 p-3 bg-gray-600/50 rounded-lg border border-gray-500">
              <div className="flex items-center justify-between text-sm text-gray-300">
                <span>
                  🚗 {t("1232", "Distance")}:{" "}
                  <strong className="text-white">
                    {distanceInfo.distance} km
                  </strong>
                </span>
                <span>
                  ⏱️ {t("1233", "Duration")}:{" "}
                  <strong className="text-white">
                    {distanceInfo.duration}
                  </strong>
                </span>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                {t("1234", "Travel Date")}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Calendar size={18} className="text-gray-400" />
                </div>
                <input
                  title={t("1235", "Select travel date")}
                  className="w-full pl-10 pr-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-md focus:outline-hidden focus:ring-2 focus:ring-(--primary) focus:border-(--primary)"
                  type="date"
                  id="booking-date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  min={getTodayDateString()}
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                {t("1236", "Travel Time")}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Clock size={18} className="text-gray-400" />
                </div>
                <input
                  title={t("1237", "Select travel time")}
                  className="w-full pl-10 pr-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-md focus:outline-hidden focus:ring-2 focus:ring-(--primary) focus:border-(--primary)"
                  type="time"
                  id="booking-time"
                  name="time"
                  value={formData.time}
                  onChange={handleChange}
                  min={
                    isToday(formData.date) ? getMinTimeForToday() : undefined
                  }
                  required
                />
              </div>
            </div>
          </div>

          {dateTimeError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{dateTimeError}</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <CustomDropdown
                options={passengerOptions}
                value={formData.passengers}
                onChange={(value) =>
                  setFormData((prev) => ({ ...prev, passengers: value }))
                }
                placeholder={t("1238", "Select passengers")}
                label={t("1239", "Number of Passengers")}
                required
                icon={<Users size={18} />}
              />
            </div>

            <div>
              <CustomDropdown
                options={luggageOptions}
                value={formData.luggage}
                onChange={(value) =>
                  setFormData((prev) => ({ ...prev, luggage: value }))
                }
                placeholder={t("1240", "Select luggage")}
                label={t("1241", "Number of Luggage")}
                required
                icon={<Package size={18} />}
              />
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-1">
              {t("1242", "Flight Number")}
              <span className="text-red-500 ml-1">*</span>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Plane size={18} className="text-gray-400" />
              </div>
              <input
                type="text"
                id="flight-number"
                name="flightNumber"
                value={formData.flightNumber}
                onChange={handleChange}
                placeholder={t("1243", "Enter flight number or 'None'")}
                className="w-full pl-10 pr-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-md focus:outline-hidden focus:ring-2 focus:ring-(--primary) focus:border-(--primary)"
                required
              />
            </div>
          </div>

          <button
            type="submit"
            className="w-full flex items-center justify-center bg-(--primary) hover:bg-(--primary-dark) text-white py-3 px-4 rounded-md font-medium transition duration-300"
          >
            {t("1244", "Continue")} <ArrowRight size={18} className="ml-2" />
          </button>
        </form>
      )}

      {step === 2 && (
        <div className="space-y-6">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-white mb-2">
              {t("1245", "Choose Your Vehicle")}
            </h3>
            <p className="text-gray-300">
              {t(
                "1246",
                "Select from our premium Mercedes-Benz fleet with transparent pricing"
              )}
            </p>
          </div>

          {vehiclePricing.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {DEFAULT_VEHICLES.map((vehicle) => {
                const pricing = vehiclePricing.find(
                  (p) => p.vehicleId === vehicle.id
                );
                if (!pricing) return null;

                const breakdown = getPriceBreakdown(pricing);
                const isSelected = selectedVehicle?.id === vehicle.id;

                return (
                  <div
                    key={vehicle.id}
                    className={`cursor-pointer bg-gray-800/50 backdrop-blur-xs rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 p-6 border-2 ${
                      isSelected
                        ? "border-blue-500 bg-blue-900/20"
                        : "border-gray-700 hover:border-gray-600"
                    }`}
                    onClick={() => handleVehicleSelect(vehicle)}
                  >
                    <div className="relative">
                      <img
                        src={vehicle.image}
                        alt={vehicle.name}
                        className="w-full h-32 object-cover rounded-lg mb-4"
                      />
                      <div className="absolute top-2 right-2 bg-linear-to-r from-blue-600 to-blue-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                        {vehicle.type}
                      </div>
                    </div>

                    <h4 className="text-lg font-bold text-white mb-2">
                      {vehicle.name}
                    </h4>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="flex items-center space-x-2 text-gray-300">
                        <Users className="h-4 w-4" />
                        <span className="text-sm">
                          {vehicle.passengers} {t("1247", "passengers")}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2 text-gray-300">
                        <Luggage className="h-4 w-4" />
                        <span className="text-sm">
                          {vehicle.luggage} {t("1248", "luggage")}
                        </span>
                      </div>
                    </div>

                    <div className="bg-gray-700/50 rounded-lg p-4 mb-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-300">
                          {t("1249", "Base Price:")}
                        </span>
                        <span className="text-white">{breakdown.base}</span>
                      </div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-300">
                          {t("1250", "Distance")} ({pricing.distance} km):
                        </span>
                        <span className="text-white">{breakdown.distance}</span>
                      </div>
                      <div className="flex justify-between items-center mb-3">
                        <span className="text-gray-300">
                          {t("1251", "Time")} ({pricing.duration}):
                        </span>
                        <span className="text-white">{breakdown.time}</span>
                      </div>
                      <div className="border-t border-gray-600 pt-2">
                        <div className="flex justify-between items-center">
                          <span className="font-semibold text-white">
                            {t("1252", "Total Price:")}
                          </span>
                          <span className="text-2xl font-bold text-blue-400">
                            {breakdown.final}
                          </span>
                        </div>
                        {breakdown.isMinimumApplied && (
                          <p className="text-xs text-yellow-400 mt-1">
                            {t("1253", "* Minimum price applied")}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 mb-4">
                      {vehicle.features.map((feature, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-gray-600 text-gray-200 rounded-full text-xs"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>

                    <button
                      type="button"
                      className={`w-full py-3 rounded-lg font-medium transition-all duration-300 ${
                        isSelected
                          ? "bg-blue-600 hover:bg-blue-700 text-white"
                          : "bg-gray-700 hover:bg-gray-600 text-gray-200"
                      }`}
                    >
                      {isSelected
                        ? t("1254", "Selected")
                        : t("1255", "Select Vehicle")}
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <Car className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-400">
                {t(
                  "1256",
                  "Please select pickup and destination to see available vehicles and pricing"
                )}
              </p>
            </div>
          )}

          <div className="flex gap-3 mt-8">
            <button
              type="button"
              onClick={() => setStep(1)}
              className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 py-3 px-4 rounded-md font-medium transition duration-300"
            >
              {t("1257", "Back to Trip Details")}
            </button>
            {selectedVehicle && (
              <button
                type="button"
                onClick={() => setStep(3)}
                className="flex-1 bg-(--primary) hover:bg-(--primary-dark) text-white py-3 px-4 rounded-md font-medium transition duration-300"
              >
                {t("1258", "Continue to Contact Info")}
              </button>
            )}
          </div>
        </div>
      )}

      {step === 3 && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleConfirm();
          }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                {t("1259", "First Name")}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User size={18} className="text-gray-400" />
                </div>
                <input
                  title={t("1260", "Enter your first name")}
                  className="w-full pl-10 pr-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-md focus:outline-hidden focus:ring-2 focus:ring-(--primary) focus:border-(--primary)"
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                {t("1261", "Last Name")}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                title={t("1262", "Enter your last name")}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-md focus:outline-hidden focus:ring-2 focus:ring-(--primary) focus:border-(--primary)"
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-300 mb-1">
              {t("1263", "Email Address")}
              <span className="text-red-500 ml-1">*</span>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail size={18} className="text-gray-400" />
              </div>
              <input
                title={t("1264", "Enter your email address")}
                className="w-full pl-10 pr-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-md focus:outline-hidden focus:ring-2 focus:ring-(--primary) focus:border-(--primary)"
                type="email"
                id="contactEmail"
                name="contactEmail"
                value={formData.contactEmail}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="mb-6">
            <PhoneInput
              value={formData.contactPhone}
              onChange={(value) =>
                setFormData((prev) => ({ ...prev, contactPhone: value }))
              }
              label={t("1265", "Phone Number")}
              id="contactPhone"
              required
              placeholder="612345678"
            />
          </div>

          <div className="flex gap-3 mb-6">
            <button
              type="button"
              onClick={() => setStep(2)}
              className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 px-4 rounded-md font-medium transition duration-300"
            >
              {t("1266", "Back to Vehicle Selection")}
            </button>
            <button
              type="submit"
              className="flex-1 bg-(--primary) hover:bg-(--primary-dark) text-white py-2 px-4 rounded-md font-medium transition duration-300"
            >
              {t("1267", "Review Booking")}
            </button>
          </div>
        </form>
      )}

      {step === 4 && (
        <div className="space-y-6">
          <div className="bg-gray-700/50 p-6 rounded-lg border border-gray-600">
            <h3 className="text-lg font-semibold text-white mb-4">
              {t("1268", "Booking Summary")}
            </h3>

            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-400">{t("1269", "From:")}</span>
                <span className="font-medium text-white">
                  {formData.pickup}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">{t("1270", "To:")}</span>
                <span className="font-medium text-white">
                  {formData.destination}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">
                  {t("1271", "Date & Time:")}
                </span>
                <span className="font-medium text-white">
                  {formData.date} at {formData.time}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">
                  {t("1272", "Passengers:")}
                </span>
                <span className="font-medium text-white">
                  {formData.passengers}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">{t("1273", "Luggage:")}</span>
                <span className="font-medium text-white">
                  {formData.luggage}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">{t("1274", "Flight:")}</span>
                <span className="font-medium text-white">
                  {formData.flightNumber}
                </span>
              </div>
              {distanceInfo && (
                <div className="flex justify-between">
                  <span className="text-gray-400">
                    {t("1275", "Distance:")}
                  </span>
                  <span className="font-medium text-white">
                    {distanceInfo.distance} km ({distanceInfo.duration})
                  </span>
                </div>
              )}
              {selectedVehicle && (
                <>
                  <div className="flex justify-between">
                    <span className="text-gray-400">
                      {t("1276", "Vehicle:")}
                    </span>
                    <span className="font-medium text-white">
                      {selectedVehicle.name}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">
                      {t("1277", "Vehicle Type:")}
                    </span>
                    <span className="font-medium text-white">
                      {selectedVehicle.type}
                    </span>
                  </div>
                  {vehiclePricing.find(
                    (p) => p.vehicleId === selectedVehicle.id
                  ) && (
                    <div className="bg-gray-600/50 rounded-lg p-4 mt-4">
                      <div className="text-white font-semibold mb-2">
                        {t("1278", "Pricing Breakdown:")}
                      </div>
                      {(() => {
                        const pricing = vehiclePricing.find(
                          (p) => p.vehicleId === selectedVehicle.id
                        )!;
                        const breakdown = getPriceBreakdown(pricing);
                        return (
                          <>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-gray-300">
                                {t("1279", "Base Price:")}
                              </span>
                              <span className="text-white">
                                {breakdown.base}
                              </span>
                            </div>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-gray-300">
                                {t("1280", "Distance:")}
                              </span>
                              <span className="text-white">
                                {breakdown.distance}
                              </span>
                            </div>
                            <div className="flex justify-between text-sm mb-2">
                              <span className="text-gray-300">
                                {t("1281", "Time:")}
                              </span>
                              <span className="text-white">
                                {breakdown.time}
                              </span>
                            </div>
                            <div className="border-t border-gray-500 pt-2">
                              <div className="flex justify-between">
                                <span className="font-semibold text-white">
                                  {t("1282", "Total Price:")}
                                </span>
                                <span className="font-bold text-xl text-blue-400">
                                  {breakdown.final}
                                </span>
                              </div>
                              {breakdown.isMinimumApplied && (
                                <p className="text-xs text-yellow-400 mt-1">
                                  {t("1283", "* Minimum price applied")}
                                </p>
                              )}
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  )}
                </>
              )}
              <div className="flex justify-between">
                <span className="text-gray-400">{t("1284", "Contact:")}</span>
                <span className="font-medium text-white">
                  {formData.firstName} {formData.lastName}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">{t("1285", "Email:")}</span>
                <span className="font-medium text-white">
                  {formData.contactEmail}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">{t("1286", "Phone:")}</span>
                <span className="font-medium text-white">
                  {formData.contactPhone}
                </span>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleEdit}
              className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 px-4 rounded-md font-medium transition duration-300"
            >
              {t("1287", "Edit Details")}
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="flex-1 bg-(--primary) hover:bg-(--primary-dark) disabled:bg-gray-400 text-white py-2 px-4 rounded-md font-medium transition duration-300"
            >
              {isSubmitting
                ? t("1288", "Sending...")
                : t("1289", "Confirm Booking")}
            </button>
          </div>
        </div>
      )}

      <div className="text-xs text-gray-400 text-center mt-4">
        {t("1290", "By submitting, you agree to our Terms & Privacy Policy")}
      </div>
    </div>
  );
};

export default BookingForm;
